import { useCallback, useMemo, useState } from "react";
import type { OpenStatusDecision } from "./types";

/**
 * Persistence approach for the "N employees waiting for decision" banner.
 *
 * The dismissed set is the set of decision nurseIds the admin has already seen
 * and chosen to hide. We persist it (per department + user) in localStorage.
 * The banner re-shows whenever the current open-decision set contains a nurseId
 * NOT in the dismissed set — i.e. when a NEW employee needs a decision — per the
 * ticket. Dismissing again snapshots the current set, so it stays hidden until
 * the set grows. nurseIds that drop out of the open set are pruned from the
 * dismissed set so a later re-trigger for the same employee re-shows the banner.
 */
export function buildDismissalKey(departmentId: string, userId: string | number): string {
	return `team.statusDecisions.dismissed_${departmentId}_${userId}`;
}

/**
 * Pure core of the banner visibility rule, extracted for unit testing.
 * Returns the pruned dismissed set (only ids still open) and whether the banner
 * should show (there is at least one open decision whose nurseId was NOT
 * already dismissed).
 */
export function computeBannerVisibility(decisionNurseIds: number[], storedDismissedIds: number[]): { dismissedIds: number[]; isBannerVisible: boolean } {
	const openSet = new Set(decisionNurseIds);
	const dismissedIds = storedDismissedIds.filter((id) => openSet.has(id));
	const dismissedSet = new Set(dismissedIds);
	const hasUndismissed = decisionNurseIds.some((id) => !dismissedSet.has(id));
	return { dismissedIds, isBannerVisible: decisionNurseIds.length > 0 && hasUndismissed };
}

function readDismissed(key: string): number[] {
	try {
		const raw = localStorage.getItem(key);
		if (!raw) {
			return [];
		}
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === "number") : [];
	} catch {
		return [];
	}
}

export type StatusDecisionsBannerState = {
	count: number;
	isBannerVisible: boolean;
	dismiss: () => void;
};

export function useStatusDecisionsBanner(
	decisions: OpenStatusDecision[] | undefined,
	departmentId?: string,
	userId?: string | number,
): StatusDecisionsBannerState {
	const decisionNurseIds = useMemo(() => (decisions ? decisions.map((d) => d.nurseId) : []), [decisions]);
	const storageKey = departmentId && userId !== undefined ? buildDismissalKey(departmentId, userId) : null;

	const [dismissedTick, setDismissedTick] = useState(0);

	const isBannerVisible = useMemo(() => {
		if (!storageKey) {
			return decisionNurseIds.length > 0;
		}
		void dismissedTick;
		return computeBannerVisibility(decisionNurseIds, readDismissed(storageKey)).isBannerVisible;
	}, [storageKey, decisionNurseIds, dismissedTick]);

	const dismiss = useCallback(() => {
		if (!storageKey) {
			return;
		}
		localStorage.setItem(storageKey, JSON.stringify(decisionNurseIds));
		setDismissedTick((tick) => tick + 1);
	}, [storageKey, decisionNurseIds]);

	return {
		count: decisionNurseIds.length,
		isBannerVisible,
		dismiss,
	};
}
